import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Calendar as CalendarIcon, 
  MapPin, 
  Trophy, 
  Users, 
  GraduationCap,
  Heart,
  Download,
  ExternalLink,
  Loader2,
  Plus,
  Send,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const typeIcons = {
  tournament: Trophy,
  expo: Users,
  social: Heart,
  league: Users,
  clinic: GraduationCap,
  gala: Heart,
};

const typeColors = {
  tournament: 'bg-blue-100 text-blue-800',
  expo: 'bg-purple-100 text-purple-800',
  social: 'bg-green-100 text-green-800',
  league: 'bg-amber-100 text-amber-800',
  clinic: 'bg-teal-100 text-teal-800',
  gala: 'bg-pink-100 text-pink-800',
};

const eventTypeOptions = [
  { value: 'tournament', label: 'Tournament' },
  { value: 'social', label: 'Social / Scramble' },
  { value: 'clinic', label: 'Clinic / Lesson' },
  { value: 'league', label: 'League Round' },
  { value: 'expo', label: 'Expo / Conference' },
  { value: 'gala', label: 'Gala / Awards' },
];

const states = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY'
];

const emptyForm = {
  title: '', type: 'social', city: '', state: '', start_at: '', end_at: '',
  description: '', entry_fee: '', format: '', capacity: '', registration_url: '',
  organizer_name: '', organizer_email: '', organizer_phone: ''
};

const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/events`);
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCalendar = (event) => {
    const start = event.start_at?.replace(/[-:]/g, '').split('.')[0] + 'Z';
    const end = event.end_at?.replace(/[-:]/g, '').split('.')[0] + 'Z';
    const title = encodeURIComponent(event.title || '');
    const details = encodeURIComponent(event.description || '');
    const location = encodeURIComponent(`${event.city}, ${event.state}`);
    window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`, '_blank');
  };

  const handleDownloadICal = (event) => {
    window.open(`${API_URL}/events/${event.event_id || event.id}/ical`, '_blank');
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    if (!form.title || !form.city || !form.state || !form.start_at || !form.end_at || !form.organizer_name || !form.organizer_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        entry_fee: form.entry_fee ? parseFloat(form.entry_fee) : null,
        capacity: form.capacity ? parseInt(form.capacity) : null,
      };
      await axios.post(`${API_URL}/events/submit`, payload);
      setSubmitted(true);
      toast.success('Event submitted for review!');
    } catch (err) {
      toast.error('Failed to submit event. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setSubmitted(false);
    setDialogOpen(false);
  };

  const majorOrgs = ['UGA', 'APGA', 'AAGD', 'National Black Golf'];
  const majorEvents = events.filter(e => majorOrgs.some(org => (e.title || '').includes(org) || (e.title || '').toLowerCase().includes(org.toLowerCase())));
  const localEvents = events.filter(e => !majorOrgs.some(org => (e.title || '').includes(org) || (e.title || '').toLowerCase().includes(org.toLowerCase())));
  const displayEvents = view === 'major' ? majorEvents : view === 'local' ? localEvents : events;

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-12 mb-8">
        <div className="container-padding flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-2">Stay Connected</p>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-3" data-testid="calendar-title">
              Unified Calendar
            </h1>
            <p className="text-cream/70 font-body max-w-2xl">
              Every major tournament, local scramble, and community event in one place. Never miss a registration deadline.
            </p>
          </div>

          {/* Submit Event Button */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-gold shrink-0" data-testid="submit-event-btn">
                <Plus className="w-4 h-4 mr-2" />
                Submit Your Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-display text-xl text-deep-green">Submit Your Event</DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm">
                  Submit your tournament, scramble, clinic, or social round. Events are reviewed before appearing on the calendar.
                </DialogDescription>
              </DialogHeader>

              {submitted ? (
                <div className="py-8 text-center">
                  <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
                  <h3 className="font-display font-bold text-xl text-deep-green mb-2">Event Submitted!</h3>
                  <p className="text-muted-foreground font-body mb-6">
                    Your event has been submitted to our review team. You'll be notified once it's approved and live on the calendar.
                  </p>
                  <Button className="btn-gold" onClick={resetForm} data-testid="submit-another-btn">
                    Submit Another Event
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmitEvent} className="space-y-5 mt-4">
                  {/* Event Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 space-y-2">
                      <Label>Event Name *</Label>
                      <Input value={form.title} onChange={(e) => handleFormChange('title', e.target.value)} placeholder="e.g., Saturday Buddy Round at Langston" data-testid="event-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Event Type *</Label>
                      <Select value={form.type} onValueChange={(v) => handleFormChange('type', v)}>
                        <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {eventTypeOptions.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Format</Label>
                      <Select value={form.format || 'none'} onValueChange={(v) => handleFormChange('format', v === 'none' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder="Select format" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None specified</SelectItem>
                          <SelectItem value="stroke">Stroke Play</SelectItem>
                          <SelectItem value="scramble">Scramble</SelectItem>
                          <SelectItem value="best ball">Best Ball</SelectItem>
                          <SelectItem value="match">Match Play</SelectItem>
                          <SelectItem value="9-hole social">9-Hole Social</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>City *</Label>
                      <Input value={form.city} onChange={(e) => handleFormChange('city', e.target.value)} placeholder="e.g., Atlanta" data-testid="event-city-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>State *</Label>
                      <Select value={form.state || 'pick'} onValueChange={(v) => handleFormChange('state', v === 'pick' ? '' : v)}>
                        <SelectTrigger data-testid="event-state-select"><SelectValue placeholder="Select state" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pick">Select state</SelectItem>
                          {states.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Date/Time */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date & Time *</Label>
                      <Input type="datetime-local" value={form.start_at} onChange={(e) => handleFormChange('start_at', e.target.value)} data-testid="event-start-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date & Time *</Label>
                      <Input type="datetime-local" value={form.end_at} onChange={(e) => handleFormChange('end_at', e.target.value)} data-testid="event-end-input" />
                    </div>
                  </div>

                  {/* Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Entry Fee ($)</Label>
                      <Input type="number" min="0" step="0.01" value={form.entry_fee} onChange={(e) => handleFormChange('entry_fee', e.target.value)} placeholder="0 for free" />
                    </div>
                    <div className="space-y-2">
                      <Label>Max Capacity</Label>
                      <Input type="number" min="1" value={form.capacity} onChange={(e) => handleFormChange('capacity', e.target.value)} placeholder="e.g., 48" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description *</Label>
                    <Textarea value={form.description} onChange={(e) => handleFormChange('description', e.target.value)} placeholder="Tell us about your event..." rows={3} data-testid="event-description-input" />
                  </div>

                  <div className="space-y-2">
                    <Label>Registration URL</Label>
                    <Input value={form.registration_url} onChange={(e) => handleFormChange('registration_url', e.target.value)} placeholder="https://..." />
                  </div>

                  {/* Organizer Info */}
                  <div className="border-t pt-4">
                    <p className="font-display font-semibold text-deep-green mb-3">Organizer Information</p>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input value={form.organizer_name} onChange={(e) => handleFormChange('organizer_name', e.target.value)} placeholder="Your name" data-testid="event-organizer-name" />
                      </div>
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input type="email" value={form.organizer_email} onChange={(e) => handleFormChange('organizer_email', e.target.value)} placeholder="you@example.com" data-testid="event-organizer-email" />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input value={form.organizer_phone} onChange={(e) => handleFormChange('organizer_phone', e.target.value)} placeholder="(555) 123-4567" />
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full btn-gold py-6" disabled={submitting} data-testid="event-submit-form-btn">
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Submit for Review</>
                    )}
                  </Button>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="container-padding">
        {/* View Tabs */}
        <Tabs defaultValue="all" className="mb-8" onValueChange={setView}>
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="all" data-testid="calendar-tab-all">All Events ({events.length})</TabsTrigger>
            <TabsTrigger value="major" data-testid="calendar-tab-major">Major ({majorEvents.length})</TabsTrigger>
            <TabsTrigger value="local" data-testid="calendar-tab-local">Local ({localEvents.length})</TabsTrigger>
          </TabsList>
        </Tabs>

        {view === 'major' && (
          <div className="mb-6 p-4 bg-gold/10 border border-gold/30 rounded-sm">
            <p className="text-deep-green font-body text-sm">
              <strong>Major Events</strong> — National tournaments and championships from UGA, APGA, AAGD, and other premier organizations.
            </p>
          </div>
        )}

        {view === 'local' && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-sm">
            <p className="text-deep-green font-body text-sm">
              <strong>Local Events</strong> — Club scrambles, buddy rounds, clinics, and community gatherings near you.
            </p>
          </div>
        )}

        {/* Events List */}
        <div className="space-y-4">
          {displayEvents.map((event) => {
            const Icon = typeIcons[event.type] || CalendarIcon;
            const colorClass = typeColors[event.type] || 'bg-gray-100 text-gray-800';
            const startDate = new Date(event.start_at);
            const endDate = new Date(event.end_at);
            
            return (
              <Card key={event.event_id || event.id} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow" data-testid="calendar-event-card">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    {/* Date Block */}
                    <div className="flex-shrink-0 w-20 text-center bg-fairway rounded-sm p-3">
                      <p className="text-gold font-bold text-xs uppercase">{startDate.toLocaleDateString('en-US', { month: 'short' })}</p>
                      <p className="text-white font-display font-bold text-2xl">{startDate.getDate()}</p>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={colorClass}>
                          <Icon className="w-3 h-3 mr-1" />
                          {event.type?.charAt(0).toUpperCase() + event.type?.slice(1)}
                        </Badge>
                        {event.entry_fee === 0 && <Badge className="bg-green-100 text-green-800">Free</Badge>}
                      </div>
                      <Link to={`/events/${event.event_id || event.id}`}>
                        <h3 className="font-display font-semibold text-lg text-deep-green hover:text-gold transition-colors line-clamp-1">
                          {event.title}
                        </h3>
                      </Link>
                      <div className="flex flex-wrap items-center gap-4 mt-1 text-muted-foreground text-sm">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {event.city}, {event.state}</span>
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {event.end_at !== event.start_at && ` - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        </span>
                        {event.entry_fee > 0 && <span className="text-gold font-semibold">${event.entry_fee}</span>}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-fairway text-fairway hover:bg-fairway hover:text-white text-xs"
                        onClick={() => handleAddToCalendar(event)}
                        data-testid="add-to-google-cal"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" />
                        Google Cal
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-300 text-gray-600 hover:bg-gray-100 text-xs"
                        onClick={() => handleDownloadICal(event)}
                        data-testid="download-ical"
                      >
                        <Download className="w-3 h-3 mr-1" />
                        iCal
                      </Button>
                      {event.registration_url && (
                        <a href={event.registration_url} target="_blank" rel="noopener noreferrer">
                          <Button size="sm" className="btn-gold text-xs">Register</Button>
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {displayEvents.length === 0 && (
          <div className="text-center py-16 text-muted-foreground">
            <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-display text-lg">No events found for this view</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
